import * as React from "react";
import { CheckIcon, PlusCircledIcon } from "@radix-ui/react-icons";
import { Column } from "@tanstack/react-table";
import { format, isValid, parseISO } from "date-fns";
import { DateRange } from "react-day-picker";

import { cn } from "../../../utils";
import { Badge } from "../badge";
import { Button } from "../button";
import { Calendar } from "../calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../command";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Separator } from "../separator";

export interface DataTableDateFilterOption {
  label: string;
  value: string;
  icon?: React.ComponentType<React.PropsWithChildren<{ className?: string }>>;
}

export interface DataTableDateFilter<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: DataTableDateFilterOption[];
}

export function DataTableDateFilter<TData, TValue>({
  column,
  title,
  options,
}: DataTableDateFilter<TData, TValue>) {
  const facets = column?.getFacetedUniqueValues();
  const [showDatePicker, setShowDatePicker] = React.useState(false);
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>();
  const [selectedOption, setSelectedOption] = React.useState<string | null>(
    null
  );

  const columnFilterValue = column?.getFilterValue();

  const isDateRange =
    typeof columnFilterValue === "object" && !Array.isArray(columnFilterValue);
  const isPresetOption =
    Array.isArray(columnFilterValue) && columnFilterValue.length > 0;

  React.useEffect(() => {
    if (isDateRange) {
      const { from, to } = columnFilterValue as { from?: string; to?: string };
      if (from || to) {
        setDateRange({
          from: from ? new Date(from) : undefined,
          to: to ? new Date(to) : undefined,
        });
        setShowDatePicker(true);
        setSelectedOption("custom-range");
      }
    } else if (isPresetOption) {
      setSelectedOption((columnFilterValue as string[])[0]);
      setShowDatePicker(false);
    } else {
      setSelectedOption(null);
      setDateRange(undefined);
      setShowDatePicker(false);
    }
  }, [columnFilterValue]);

  const handleSelectOption = (value: string) => {
    if (selectedOption === value) {
      setShowDatePicker(false);
      setSelectedOption(null);
      column?.setFilterValue(undefined);
      if (value === "custom-range") {
        setDateRange(undefined);
      }
      return;
    }

    if (value === "custom-range") {
      setShowDatePicker(true);
      setSelectedOption("custom-range");
    } else {
      setShowDatePicker(false);
      setSelectedOption(value);
      column?.setFilterValue([value]);
    }
  };

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setDateRange(range);

    if (!range || (range.from && range.to)) {
      if (range?.from || range?.to) {
        const filterValue = {
          from: range.from ? format(range.from, "yyyy-MM-dd") : undefined,
          to: range.to ? format(range.to, "yyyy-MM-dd") : undefined,
        };
        column?.setFilterValue(filterValue);
      } else {
        column?.setFilterValue(undefined);
        setSelectedOption(null);
      }
    }
  };

  const handleClearFilters = () => {
    column?.setFilterValue(undefined);
    setSelectedOption(null);
    setDateRange(undefined);
    setShowDatePicker(false);
  };

  const getOptionCount = (optionValue: string) => {
    if (!column) return 0;

    const facetedRowModel = column.getFacetedRowModel();
    let count = 0;

    for (let i = 0; i < facetedRowModel.flatRows.length; i++) {
      const row = facetedRowModel.flatRows[i];
      const value = row.getValue(column.id) as string;

      if (!value) continue;

      const rowDate = parseISO(value);
      if (!isValid(rowDate)) continue;

      if (facets?.get(optionValue)) {
        return facets.get(optionValue);
      }
    }

    return count;
  };

  const getDateRangeCount = () => {
    if (!column || (!dateRange?.from && !dateRange?.to)) return 0;

    const facetedRowModel = column.getFacetedRowModel();
    let count = 0;

    return facetedRowModel.rows.length;
  };

  const getFilterDisplay = () => {
    if (selectedOption === "custom-range" && dateRange) {
      if (dateRange.from && dateRange.to) {
        return `${format(dateRange.from, "MMM d")} - ${format(
          dateRange.to,
          "MMM d"
        )}`;
      } else if (dateRange.from) {
        return `From ${format(dateRange.from, "MMM d")}`;
      } else if (dateRange.to) {
        return `Until ${format(dateRange.to, "MMM d")}`;
      }
    } else if (selectedOption) {
      const option = options.find((opt) => opt.value === selectedOption);
      return option?.label || "";
    }
    return "";
  };

  const hasActiveFilter = selectedOption !== null;
  const filterDisplay = getFilterDisplay();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircledIcon className="mr-2 h-4 w-4" />
          {title}
          {hasActiveFilter && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                1
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                <Badge
                  variant="secondary"
                  className="rounded-sm px-1 font-normal"
                >
                  {filterDisplay}
                </Badge>
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedOption === option.value;
                const count = getOptionCount(option.value);

                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleSelectOption(option.value)}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon className={cn("h-4 w-4")} />
                    </div>
                    {option.icon && (
                      <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                    {count !== undefined && count > 0 && (
                      <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                        {count}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
              <CommandItem onSelect={() => handleSelectOption("custom-range")}>
                <div
                  className={cn(
                    "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                    selectedOption === "custom-range"
                      ? "bg-primary text-primary-foreground"
                      : "opacity-50 [&_svg]:invisible"
                  )}
                >
                  <CheckIcon className={cn("h-4 w-4")} />
                </div>
                <span>Custom Range</span>
              </CommandItem>
            </CommandGroup>

            {showDatePicker && (
              <>
                <CommandSeparator />
                <div className="p-2">
                  <Calendar
                    initialFocus
                    mode="range"
                    selected={dateRange}
                    onSelect={handleDateRangeSelect}
                    numberOfMonths={2}
                  />
                </div>
              </>
            )}

            {hasActiveFilter && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={handleClearFilters}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
